import {
  CModal,
  CTable,
  CTableBody,
  CTableDataCell,
  CTableHead,
  CTableHeaderCell,
  CTableRow,
  CTooltip,
} from "@coreui/react";
import { useState } from "react";
import DeleteIcon from "../../components/icons/DeleteIcon";
import PromptModal from "../../components/modals/PromptModal";
import { colors } from "../../config/theme";
import EditIcon from "../../components/icons/EditIcon";
import PlaceAdminPartnerModal from "./NewPlaceAdminPartner";
import { useMutation, useQueryClient } from "react-query";
import { useCookie } from "../../hooks/useCookie";
import apiService from "../../service/apiService";
import { general } from "../../locales/general";
import { useToast } from "../../hooks/useToast";
import { ChartColumnStackedIcon } from "lucide-react";
import { Link } from "react-router-dom";

function TableBodyRowContent({ user }: { user: User }) {
  const { token } = useCookie("vToken");
  const { toastError, toastSuccess } = useToast();
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const queryClient = useQueryClient();
  const handleRefresh = () => {
    queryClient.invalidateQueries("getMultiPlaceAdminPartners");
  };

  const deletePlaceManager = useMutation((id: string) => {
    return apiService.MakeDeleteRequest(`users/place-manager/${id}`, token);
  });

  const removePlaceManager = (id: string) => {
    deletePlaceManager.mutate(id, {
      onError(error) {
        toastError(general.fr.message.operationFailed);
      },
      onSuccess() {
        toastSuccess(general.fr.message.userDeleted);
        queryClient.invalidateQueries(["getMultiPlaceAdminPartners"]);
      },
    });
  };

  return (
    <CTableRow
      style={{
        width: "100%",
        position: "relative",
      }}
    >
      <CTableDataCell className="px-3 text-sm">
        <div className="d-flex align-items-center gap-3">
          <img
            src={
              user.profilePicture ??
              `https://ui-avatars.com/api/?name=${user?.fullName}`
            }
            alt=""
            width={30}
            height={30}
            style={{ borderRadius: "100%" }}
            className="object-fit-cover"
          />

          <div>
            {user?.fullName}
            <p className="m-0">{user.email}</p>
          </div>
        </div>
      </CTableDataCell>
      <CTableDataCell className="p-3 text-sm">{user?.phone}</CTableDataCell>
      <CTableDataCell className="p-3 text-sm">
        <CTooltip content="Voir les statistiques">
          <Link
            to={`/place-admin-partners/${user?.id}/statistics`}
            className="outline-none border-0 bg-transparent"
          >
            <ChartColumnStackedIcon color={colors.secondary} />
          </Link>
        </CTooltip>
      </CTableDataCell>
      <CTableDataCell className="p-3 text-sm">
        <button
          onClick={() => {
            setShowEditModal(true);
          }}
          className="outline-none border-0 bg-transparent"
        >
          <EditIcon
            width="20"
            height="20"
            fill={colors.secondary}
            className="ms-2"
          />
        </button>
      </CTableDataCell>
      <CTableDataCell className="p-3 text-sm">
        <button
          onClick={() => setShowModal(true)}
          className="outline-none border-0 bg-transparent"
        >
          <DeleteIcon
            width="20"
            height="20"
            fill={colors.primary}
            className="ms-2"
          />
        </button>
      </CTableDataCell>

      <PromptModal
        open={showModal}
        onAccept={() => {
          removePlaceManager(user?.id);
          setShowModal(false);
          handleRefresh();
        }}
        onClose={() => setShowModal(false)}
      />
      <CModal
        visible={showEditModal}
        onClose={() => setShowEditModal(false)}
        size="lg"
        alignment="center"
      >
        <PlaceAdminPartnerModal
          key={user?.id}
          onClose={() => setShowEditModal(false)}
          onAccept={() => {
            handleRefresh();
            setShowEditModal(false);
          }}
          open={showEditModal}
          id={user?.id}
          user={user}
        />
      </CModal>
    </CTableRow>
  );
}

interface TableProps {
  users: User[];
}

export default function PlaceAdminPartnersTable({ users }: TableProps) {
  return (
    <div className="mt-3">
      <CTable>
        <CTableHead>
          <CTableRow>
            <CTableHeaderCell scope="col" className="text-sm">
              <div className="d-flex align-items-center justify-content-between">
                Nom{" "}
              </div>
            </CTableHeaderCell>
            <CTableHeaderCell scope="col" className="text-sm">
              <div className="d-flex align-items-center justify-content-between">
                Téléphone{" "}
              </div>
            </CTableHeaderCell>

            <CTableHeaderCell
              scope="col"
              className="text-sm"
            ></CTableHeaderCell>
            <CTableHeaderCell
              scope="col"
              className="text-sm"
            ></CTableHeaderCell>
            <CTableHeaderCell
              scope="col"
              className="text-sm"
            ></CTableHeaderCell>
          </CTableRow>
        </CTableHead>
        <CTableBody>
          {users?.map((user) => (
            <TableBodyRowContent key={user.id} user={user} />
          ))}
        </CTableBody>
      </CTable>
    </div>
  );
}
